import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@component/button";
import { ArrowLeftIcon, ClockIcon, RotateCcwIcon } from "lucide-react";
import { InterviewLeaderboardItem } from "@type/interview";
import { DashboardLayout } from "@container/layout/dashboard";
import { getCompletedPracticeInterview, getLeaderboardInterviews } from "@service/interview";
import PracticeInterviewLeaderboard from "@container/practice-interview-leaderboard";
import { getAssessment } from "@service/assessment";
import { InterviewAssessment } from "@type/assessments";
import { cn } from "@util/tailwind";
import { setCookie } from "@util/cookie";

export default function CandidateInterviewFeedbackPage() {
    const navigate = useNavigate();
    const params = useParams()
    const [isLoadingScore, setLoadingScore] = useState<boolean>(true);
    const [isLoadingLeaderboard, setLoadingLeaderboard] = useState<boolean>(true);
    const [assessmentDetail, setAssessmentDetail] = useState<InterviewAssessment | null>(null);
    const [leaderboards, setLeaderboards] = useState<InterviewLeaderboardItem[]>([]);
    const [interviewLeaderboard, setInterviewLeaderboard] = useState<InterviewLeaderboardItem | undefined>(undefined);
    const [messageIndex, setMessageIndex] = useState<number>(0);
    const [completedStatus, setCompletedStatus] = useState<string | null>(null);
    // Score sourced from /interviews/completed-practice-interviews/:id.
    // BE returns it at `data.score` (0–100 scale).
    const [completedScore, setCompletedScore] = useState<number | null>(null);

    const isPendingProcessing = completedStatus === "Pending Processing";
    const totalPoints = completedScore ?? 0;
    const isZeroScore = !isLoadingScore && totalPoints === 0;

    const loadingMessages = [
        "Generating your score...",
        "Your score will be ready in ~5 minutes.",
        "Analyzing your responses...",
        "Comparing with other candidates...",
    ];

    const fetchAssessmentDetail = async (interviewId: string) => {
        const response = await getAssessment(interviewId);
        setAssessmentDetail(response as InterviewAssessment);
    }

    useEffect(() => {
        if (isLoadingScore) {
            const interval = setInterval(() => {
                setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
            }, 2500); // Change message every 2.5 seconds

            return () => clearInterval(interval);
        }
    }, [isLoadingScore]);

    useEffect(() => {
        if (params.interviewid) {
            getLeaderboardInterviews(params.interviewid).then((resp) => {
                if (resp.status === 200) {
                    setLeaderboards(resp.data.data.leaderboard);
                }
            }).finally(() => {
                setLoadingLeaderboard(false);
            })
            fetchAssessmentDetail(params.interviewid)

            getCompletedPracticeInterview(params.interviewid).then((resp) => {
                if (resp.code === 200 && resp.data?.status) {
                    setCompletedStatus(resp.data.status);
                    if (typeof resp.data.score === "number") {
                        setCompletedScore(resp.data.score);
                        setLoadingScore(false);
                    } else if (resp.data.status !== "Pending Processing") {
                        // Non-pending status without a score — surface a 0
                        // so the UI exits the loading state instead of
                        // spinning forever.
                        setCompletedScore(0);
                        setLoadingScore(false);
                    }
                }
            });
        }
    }, [params.interviewid])

    const fetchLeaderboard = (interviewid: string) => {
        // Leaderboard now only drives the "top N%" indicator — the
        // canonical score comes from getCompletedPracticeInterview.
        getLeaderboardInterviews(interviewid!, true).then((resp) => {
            if (resp.status === 200) {
                const leaderboardData = resp.data.data.leaderboard[0];
                setInterviewLeaderboard(leaderboardData);
                setCookie(`interview_score_feedback:${interviewid}`, JSON.stringify(leaderboardData));
            }
        }).catch((error) => {
            console.error('Error fetching leaderboard:', error);
        });
    };

    // Separate useEffect for score polling
    useEffect(() => {
        if (!params.interviewid) return;
        fetchLeaderboard(params.interviewid);
    }, [params.interviewid])

    // Poll the completed-practice-interview status while it's still processing.
    // Once the BE flips it to "Complete" (or anything non-pending), the effect's
    // condition becomes false and the interval is torn down automatically.
    useEffect(() => {
        if (!isPendingProcessing || !params.interviewid) return;

        const intervalId = setInterval(() => {
            getCompletedPracticeInterview(params.interviewid!).then((resp) => {
                if (resp.code === 200 && resp.data?.status) {
                    setCompletedStatus(resp.data.status);
                    if (typeof resp.data.score === "number") {
                        setCompletedScore(resp.data.score);
                        setLoadingScore(false);
                    }
                    if (resp.data.status !== "Pending Processing") {
                        if (typeof resp.data.score !== "number") {
                            // Status flipped but payload has no score —
                            // unblock the UI either way.
                            setCompletedScore((prev) => prev ?? 0);
                            setLoadingScore(false);
                        }
                        fetchLeaderboard(params.interviewid!)
                    }
                }
            });
        }, 10_000);

        return () => clearInterval(intervalId);
    }, [isPendingProcessing, params.interviewid]);

    return (
        <DashboardLayout isDisabledFlex>
            <div className="flex flex-col sm:flex-row sm:h-screen">
                <div className="w-full sm:w-1/2 p-4 sm:p-8">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="mb-4 text-gray-600 hover:text-gray-900 hover:bg-gray-100 -ml-2"
                        onClick={() => {
                            navigate("/interview");
                        }}
                    >
                        <ArrowLeftIcon className="h-4 w-4 mr-1" />
                        Back to Interviews
                    </Button>
                    <div className="space-y-6 flex flex-col items-center justify-center h-full">
                        {isPendingProcessing ? (
                            <div className="text-center max-w-md">
                                <div className="w-14 h-14 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
                                    <ClockIcon className="w-6 h-6" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Your score is being processed.
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {assessmentDetail?.content?.interviewName
                                        ? `${assessmentDetail.content.interviewName} — `
                                        : ""}
                                    we're still analyzing your interview. <br /> Come back in a few minutes to see your performance.
                                </p>
                                <div className="flex mt-4 gap-3 justify-center items-center flex-wrap">
                                    <Link
                                        className="flex items-center text-sm font-medium text-blue-500 hover:text-black transition-colors"
                                        to={`/interview/${params.interviewid}/detail`}
                                    >
                                        <Button
                                            variant="ghost"
                                            className="p-0 hover:bg-transparent hover:text-black transition-colors"
                                        >
                                            View feedback →
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <>
                                    <h3 className="text-sm font-medium text-blue-600 mb-2 text-center">
                                        Your performance
                                    </h3>
                                    {isLoadingScore ? (
                                        <div className="relative w-full" style={{ height: "192px" }}>
                                            {/* Centered loading state that matches the vertical position of the loaded content */}
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="text-center">
                                                    <div className="flex space-x-2 justify-center mb-4">
                                                        {/* Three bouncing blue dots */}
                                                        <div
                                                            className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                                            style={{ animationDelay: "0ms" }}
                                                        ></div>
                                                        <div
                                                            className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                                            style={{ animationDelay: "150ms" }}
                                                        ></div>
                                                        <div
                                                            className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
                                                            style={{ animationDelay: "300ms" }}
                                                        ></div>
                                                    </div>
                                                    <p
                                                        className="text-gray-600 text-sm transition-opacity duration-300"
                                                        key={messageIndex}
                                                    >
                                                        {loadingMessages[messageIndex]}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="relative w-full">
                                            <div className="w-full text-center">
                                                <div className="text-5xl font-bold">{totalPoints} {totalPoints > 0 ? "points" : "point"}</div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="text-center">
                                        <h3 className="text-lg font-medium mb-2">{assessmentDetail?.content?.interviewName || ""}</h3>
                                        {isLoadingScore ? (
                                            <p className="text-gray-700">
                                                Your score is generating, come back in ~5 minutes
                                                <br />
                                                to see how you performed!
                                            </p>
                                        ) : isZeroScore ? (
                                            <p className="text-gray-700">
                                                Don't worry — practice makes perfect.
                                                <br />
                                                Give the interview another shot to improve your score.
                                            </p>
                                        ) : (
                                            <p className="text-gray-700">
                                                You're in the top{" "}
                                                <span className="font-bold">{interviewLeaderboard?.top_n || 0}%</span> of{" "}
                                                {leaderboards.length} people who took this interview.
                                            </p>
                                        )}
                                        <div className="flex mt-4 gap-3 justify-center items-center flex-wrap">
                                            {isZeroScore && (
                                                <Button
                                                    onClick={() =>
                                                        navigate(`/interview/${params.interviewid}/start`, {
                                                            state: { isPracticeInterview: true },
                                                        })
                                                    }
                                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                                >
                                                    <RotateCcwIcon className="w-4 h-4 mr-1.5" />
                                                    Retake interview
                                                </Button>
                                            )}
                                            <Link
                                                className={cn("flex items-center text-sm font-medium text-blue-500 hover:text-black transition-colors", {
                                                    "pointer-events-none": isLoadingScore
                                                })}
                                                to={`/interview/${params.interviewid}/detail`}
                                            >
                                                <Button
                                                    disabled={isLoadingScore}
                                                    variant="ghost"
                                                    className="p-0 hover:bg-transparent hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    View feedback →
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                            </>
                        )}
                    </div>
                </div>

                <div className="w-full sm:w-1/2 p-4 sm:p-8 sm:border-l border-gray-200 mt-8 sm:mt-0 pb-24 sm:pb-10">
                    <PracticeInterviewLeaderboard
                        isLoading={isLoadingLeaderboard}
                        interviewName={assessmentDetail?.content.interviewName || ""}
                        items={leaderboards}
                        isLoadingScore={isLoadingScore}
                        isPendingProcessing={isPendingProcessing}
                    />
                </div>
            </div>
        </DashboardLayout>
    )
}